import React from "react";
import { format } from "lib/globalize-util";
import { ConfirmDialog } from "components/dialog";
import { promotionsService } from "containers/admin/promotions/services/promotions";

import TextField from "components/form/text-field";
import { Form } from "formsy-react";
import { IStore } from "reducers";
import { add as snackbarAdd } from "actions/snackbars";
import { connect } from "react-redux";
import CircularProgress from "material-ui/CircularProgress";
import { useCheckPromotionName } from "containers/admin/promotions/hooks/useCheckPromotionName";
import { useStyles, styles } from "./styles";
import { useDebounceFunction as useDebounce } from "hooks";

import { v4 as uuid } from "uuid";
import { DATATEST } from "constants/integration";
import { IDuplicateActions, IDuplicateModal, IDuplicateModalProps } from "./interfaces";
import {
    updateDuplicatedEntity,
    updateDuplicatedThresholdEntity,
} from "containers/admin/promotions/modals/helpers/updateDuplicatedEntity";

export const DuplicateModal: React.FC<
    IDuplicateModal & IDuplicateActions & Omit<IDuplicateModalProps, "onConfirm">
> = ({
    open,
    onClose,
    currentPromotion,
    thresholdEntityPromotion,
    merchantId,
    currentPromotionLoading,
    onSnackbarAdd,
    onSuccess,
}) => {
    const classes = useStyles();
    const [promotionName, setPromotionName] = React.useState<string>("");
    const [promotionExist, setPromotionExist] = React.useState<boolean>(true);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);

    const onChangePromotionName = React.useCallback((event: unknown, name: string) => setPromotionName(name), []);
    const onChangePromotionNameDebounced = useDebounce(onChangePromotionName, 500);

    const nameErrorText = promotionExist ? format("This name is taken") : "";

    React.useEffect(() => setPromotionName(`Copy of - ${currentPromotion?.name}`), [currentPromotion]);

    const { data } = useCheckPromotionName(promotionName, merchantId);

    if (data !== promotionExist) {
        setPromotionExist(data);
    }

    const onConfirmAction = React.useCallback(async () => {
        if (promotionExist) {
            onSnackbarAdd({ message: "We can not create promotion with the same name" });
            return;
        }

        setIsLoading(true);

        try {
            const newId = uuid();
            const updatedEntity = updateDuplicatedEntity(currentPromotion, promotionName, newId);
            const includeConditionalRule = !!(
                updatedEntity.price_condition?.conditional_date_range_condition?.date_from &&
                updatedEntity.price_condition?.conditional_date_range_condition?.date_to
            );
            await promotionsService.callCreatePromotionPoint(
                merchantId,
                JSON.stringify(updatedEntity),
                includeConditionalRule,
            );

            if (thresholdEntityPromotion) {
                const updatedThresholdEntity = updateDuplicatedThresholdEntity(
                    thresholdEntityPromotion,
                    promotionName,
                    newId,
                );
                const includeConditionalRule = !!(
                    updatedThresholdEntity.price_condition?.conditional_date_range_condition?.date_from &&
                    updatedThresholdEntity.price_condition?.conditional_date_range_condition?.date_to
                );
                await promotionsService.callCreatePromotionPoint(
                    merchantId,
                    JSON.stringify(updatedThresholdEntity),
                    includeConditionalRule,
                );
            }

            onSuccess?.(updatedEntity);
            onSnackbarAdd({ message: format("Promotion successfully duplicated") });
        } catch (e) {
            onSnackbarAdd({ message: format("Error duplicate promotion. Try again later") });
        } finally {
            onClose();
        }
        setIsLoading(false);
    }, [
        promotionExist,
        onSnackbarAdd,
        currentPromotion,
        promotionName,
        merchantId,
        thresholdEntityPromotion,
        onSuccess,
        onClose,
    ]);

    if (currentPromotionLoading && open) {
        return (
            <div className={classes.loading}>
                <CircularProgress data-test={DATATEST.PROMOTIONS.CIRCULAR_PROGRESS} />
            </div>
        );
    }

    return (
        <ConfirmDialog
            dataTestConfirm={DATATEST.PROMOTIONS.DUPLICATE_MODAL_DIALOG_CONFIRM}
            dataTestCancel={DATATEST.PROMOTIONS.DUPLICATE_MODAL_DIALOG_CANCEL}
            open={open}
            onConfirm={onConfirmAction}
            onCancel={onClose}
            onRequestClose={onClose}
            contentStyle={styles.confirmDialog}
            title={format("Duplicate")}
            cancelLabel={format("Cancel")}
            confirmLabel={format("Save")}
            disabledConfirm={isLoading}
        >
            <Form>
                <TextField
                    dataTest={DATATEST.PROMOTIONS.DUPLICATE_MODAL_TEXTFIELD}
                    errorText={nameErrorText}
                    onChange={onChangePromotionNameDebounced}
                    label={format("Promotion name")}
                    placeholder={format("Enter name")}
                    name="promotion_name"
                    defaultValue={`Copy of - ${currentPromotion?.name}`}
                    fullWidth
                    required
                />
            </Form>
        </ConfirmDialog>
    );
};

const mapStateToProps = (state: IStore): IDuplicateModal => ({
    currentPromotionLoading: state.promotion.isLoading,
    currentPromotion: state.promotion.entity?.uiEntity,
    thresholdEntityPromotion: state.promotion.entity?.thresholdEntity,
    merchantId: state.scope.brand.get("merchant_id"),
});

const mapDispatchToProps = (dispatch): IDuplicateActions => ({
    onSnackbarAdd: (params: { message: string }) => dispatch(snackbarAdd(params)),
});

export const DuplicateModalContainer = connect<
    IDuplicateModal,
    IDuplicateActions,
    Omit<IDuplicateModalProps, "onConfirm">
>(
    mapStateToProps,
    mapDispatchToProps,
)(DuplicateModal);
export default DuplicateModalContainer;
